import {DEFAULT_PANEL_CONTAINERS, GATE_TYPE, PANEL_TYPE, PANEL_INSERTED_ATTR} from "../Constants";
import {SubscribePanel} from "../ui/panels/SubscribePanel";
import {PaymentPanel} from "../ui/panels/PaymentPanel";
import {DonationPanel} from "../ui/panels/DonationPanel";
import {AuthenticationPanel} from "../ui/panels/AuthenticationPanel";
import {CoverGate} from "../ui/gates/CoverGate";
import {PreviewGate} from "../ui/gates/PreviewGate";
import {evaluatePanelOptions} from "../PanelOptionsLogic";
import {walletModule} from "./WalletModule";
import {BannerGate} from "../ui/gates/BannerGate";
import {isString} from "../../stem-core/src/base/Utils";
import {PanelElement} from "../ui/elements/PanelElement";
import {merchantFunctionModule} from "./merchant-state/MerchantFunctionModule";
import {CleanupJobs} from "../../stem-core/src/base/Dispatcher";
import {isMerchantActive} from "./PublicData";
import {GateElement} from "../ui/elements/GateElement.js";


const panelMap = {
    [PANEL_TYPE.subscribe]: SubscribePanel,
    [PANEL_TYPE.payment]: PaymentPanel,
    [PANEL_TYPE.donation]: DonationPanel,
    [PANEL_TYPE.donationPage]: DonationPanel,
    [PANEL_TYPE.shop]: PanelElement,
    [PANEL_TYPE.newsletter]: PanelElement,
    [PANEL_TYPE.custom]: PanelElement,
    [PANEL_TYPE.dashboard]: PanelElement,
    [PANEL_TYPE.giftSubscription]: PanelElement,
    [PANEL_TYPE.authentication]: AuthenticationPanel,
    [PANEL_TYPE.empty]: PanelElement,
};

// TODO @Mihai merge these, just pass in some options
const gateMap = {
    [GATE_TYPE.cover]: CoverGate,
    [GATE_TYPE.preview]: PreviewGate,
    [GATE_TYPE.banner]: BannerGate,
};

class PanelModule {
    pendingRequests = [];
    rules = DEFAULT_PANEL_CONTAINERS;

    init() {
        // Pre-create the modal panel.
        const panel = new PanelElement({type: PANEL_TYPE.empty, gateType: GATE_TYPE.popup}, walletModule.widget.node);
        panel.initialize();

        this.scanForPanels();
        // TODO(@sdk-deprecated): We shouldn't automatically add an interval anymore.
        setInterval(() => this.scanForPanels(), 200);
    }

    addRule(rule) {
        this.rules.push(rule);
        // After adding a new rule, run a scan to immediately apply it.
        this.scanForPanels();
    }

    scanForPanels(rootNode = document) {
        for (const rule of this.rules) {
            for (const container of rootNode.querySelectorAll(rule.selector)) {
                if (container.getAttribute(PANEL_INSERTED_ATTR) === "true") {
                    return;
                }
                container.setAttribute(PANEL_INSERTED_ATTR, "true");
                const options = {...rule};
                delete options.selector;
                this.createPanel(container, options);
            }
        }
    }

    resolvePendingRequests() {
        const pendingRequests = this.pendingRequests;
        this.pendingRequests = null;
        for (const args of pendingRequests) {
            this.createPanel(...args);
        }
    }

    createPanel(nodesOrSelector, options) {
        // If the above method hasn't been called, this class can't create panels, so it has to queue them.
        // The place where the previous method gets called is relevant.
        if (this.pendingRequests != null) {
            this.pendingRequests.push(arguments);
            return;
        }

        // Transition from multiple possible nodes to a single one per createPanel call.
        if (isString(nodesOrSelector)) {
            nodesOrSelector = [...document.querySelectorAll(nodesOrSelector)];
        }

        // TODO @cleanup, just disable this functionality of accepting an array?
        if (Array.isArray(nodesOrSelector)) {
            // TODO @branch we're opening ourselves to a ddos here, cap the array length
            nodesOrSelector.length = Math.min(4, nodesOrSelector.length);
            for (const node of nodesOrSelector) {
                this.createPanel(node, options);
            }
            return;
        }

        // STEP 1: Build the final options and prepare to instantiate the panel.

        // Compose the options. The inheritance chain is options <- options for this panel id <- options for this panel type <- default pg options
        options = evaluatePanelOptions(options);
        const removeAnonymousFunctionJobs = new CleanupJobs();
        // All the functions added for the panel are passed through the merchant function module. We need it so that the
        // errors don't get thrown and our code doesn't break.
        for (const maybeFunctionOption of ["onFlowCheckpoint", "onFlowFinish", "onFlowSuccess", "onMount"]) {
            if (typeof options[maybeFunctionOption] === "function") {
                const functionKey = merchantFunctionModule.addAnonymous(options[maybeFunctionOption]);
                options[maybeFunctionOption] = functionKey;
                removeAnonymousFunctionJobs.add(() => merchantFunctionModule.delete(functionKey));
            }
        }

        const panelType = options.type;
        const gateType = options.gateType;

        // Prevent creating an invalid / not allowed panel.

        if (!isMerchantActive()) {
            console.error("[BlinkSDK] Blink SDK is inactive!");
            return;
        }

        if (!panelType || !panelMap[panelType]) {
            console.error("[BlinkSDK] Panel type is not supported");
            return;
        }

        // STEP 2: Instantiate and mount the panel and the gate.
        const domNode = nodesOrSelector;
        let panel, handler;
        // Popups are created in a different way because they use an already existing iframe and because that iframe
        // also contains the widget. To understand the code better, I suggest first looking at the non-popup flow and
        // then looking at the "showModal" method to see how it is adapted for the fact that it operate with an existing
        // element.
        if (gateType === GATE_TYPE.popup) {
            const modalObj = walletModule.showModal(panelMap[panelType], options);
            panel = modalObj.panel;
            handler = modalObj.handler;
        } else {
            // Create a panel, create a gate that contains that panel, mount the gate in the DOM and build the handler.
            const GateClass = gateType ? gateMap[gateType] : GateElement;

            if (GateClass == null) {
                console.error("[BlinkSDK] Unknown gate type", gateType);
                return;
            }

            panel = new panelMap[panelType](options);
            const gate = new GateClass(options, panel);
            gate.mount(domNode);
            handler = gate.getInteractiveHandlers();
        }

        // STEP 3: Handle all the listeners and the post mount logic of the panel.

        // Remove all of the panel's functions lying in the merchant function module once the panel is destroyed.
        panel.addCleanupJob(removeAnonymousFunctionJobs);

        // Map the flow functions to the corresponding listeners.
        if (options.onFlowFinish) {
            panel.flowFinish.addListener((...args) => merchantFunctionModule.call(options.onFlowFinish, ...args));
        }

        if (options.onFlowCheckpoint) {
            panel.flowCheckpoint.addListener((event) => merchantFunctionModule.call(options.onFlowCheckpoint, event));
        }

        // If the flow is a success, there are given circumstances in which the panel should be destroyed.
        // The preview, cover and banner naturally get destroyed because they obfuscate content. Currently we also
        // destroy a newsletter panel if the user did subscribe. To see what panels have this logic, just search for
        // onFlowSuccess and follow the code.
        const onFlowSuccess = () => {
            // TODO: We probably want the merchant to be able to opt-out of this behaviour?
            // Automatically remove the panel for these gate types on flow success.
            if (gateType === GATE_TYPE.preview || gateType === GATE_TYPE.cover || gateType === GATE_TYPE.banner
                || (!gateType && panelType === PANEL_TYPE.newsletter)) {
                handler.remove();
            }
            if (options.onFlowSuccess) {
                merchantFunctionModule.call(options.onFlowSuccess);
            }
        };
        // I believe that this logic can be placed earlier in the process if we want to. However, since all of this code
        // is sync, it doesn't make a difference.
        if (panel.isFlowSuccess()) {
            onFlowSuccess();
        } else {
            panel.flowSuccess.addListener(onFlowSuccess);
        }

        if (options.onMount) {
            merchantFunctionModule.call(options.onMount, handler);
        }
    }

    createPopup(options) {
        this.createPanel(document.body, {
            ...options,
            gateType: GATE_TYPE.popup,
        });
    }

    createAuthenticationPopup(options={}) {
        this.createPopup({
            ...options,
            type: PANEL_TYPE.authentication,
            skipCTA: true,
        });
    }
}

export const panelModule = new PanelModule();
